import { useCurrentUser } from '@/hooks/use-current-user';
import { useUserPreferences } from '@/hooks/use-preferences';

const DEFAULT_DATE_FORMAT = 'dd.MM.yyyy';
const DEFAULT_TIMEZONE = 'Europe/Berlin';
const DEFAULT_LOCALE = 'en-GB';
const DATE_ONLY_REGEX = /^\d{4}-\d{2}-\d{2}$/;

const formatPatternMap: Record<string, string> = {
  'dd.MM.yyyy': 'dd.MM.yyyy',
  'MM/dd/yyyy': 'MM/dd/yyyy',
  'yyyy-MM-dd': 'yyyy-MM-dd',
  'dd/MM/yyyy': 'dd/MM/yyyy',
  'MM.dd.yyyy': 'MM.dd.yyyy',
};

type UserDateFormat = keyof typeof formatPatternMap;

type FormatterParts = {
  year: string;
  month: string;
  day: string;
  hour: string;
  minute: string;
};

function getDateFormatPattern(userDateFormat?: string): UserDateFormat {
  if (!userDateFormat || !(userDateFormat in formatPatternMap)) {
    return DEFAULT_DATE_FORMAT;
  }

  return userDateFormat as UserDateFormat;
}

function normalizeTimezone(timeZone?: string): string {
  return typeof timeZone === 'string' && timeZone.trim().length > 0
    ? timeZone
    : DEFAULT_TIMEZONE;
}

function parseDateOnly(dateValue: string): FormatterParts {
  const [year, month, day] = dateValue.split('-');
  return { year, month, day, hour: '00', minute: '00' };
}

function getFormatterParts(date: Date, timeZone?: string): FormatterParts {
  const formatter = new Intl.DateTimeFormat(DEFAULT_LOCALE, {
    timeZone: normalizeTimezone(timeZone),
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  });

  const parts = formatter.formatToParts(date);
  const partMap = new Map(parts.map((part) => [part.type, part.value]));

  return {
    year: partMap.get('year') || '0000',
    month: partMap.get('month') || '00',
    day: partMap.get('day') || '00',
    hour: partMap.get('hour') || '00',
    minute: partMap.get('minute') || '00',
  };
}

function resolveFormatterParts(
  date: Date | string | null | undefined,
  timeZone?: string,
): FormatterParts | null {
  if (!date) {
    return null;
  }

  if (typeof date === 'string' && DATE_ONLY_REGEX.test(date.trim())) {
    return parseDateOnly(date.trim());
  }

  const dateObj = typeof date === 'string' ? new Date(date) : date;

  if (Number.isNaN(dateObj.getTime())) {
    return null;
  }

  return getFormatterParts(dateObj, timeZone);
}

function formatDateParts(parts: FormatterParts, userDateFormat?: string): string {
  const formatPattern = getDateFormatPattern(userDateFormat);

  switch (formatPattern) {
    case 'MM/dd/yyyy':
      return `${parts.month}/${parts.day}/${parts.year}`;
    case 'yyyy-MM-dd':
      return `${parts.year}-${parts.month}-${parts.day}`;
    case 'dd/MM/yyyy':
      return `${parts.day}/${parts.month}/${parts.year}`;
    case 'MM.dd.yyyy':
      return `${parts.month}.${parts.day}.${parts.year}`;
    case 'dd.MM.yyyy':
    default:
      return `${parts.day}.${parts.month}.${parts.year}`;
  }
}

export function formatUserDate(
  date: Date | string | null | undefined,
  userDateFormat?: string,
  timeZone?: string,
): string {
  const parts = resolveFormatterParts(date, timeZone);
  if (!parts) {
    return '-';
  }

  return formatDateParts(parts, userDateFormat);
}

export function formatUserTime(
  date: Date | string | null | undefined,
  timeZone?: string,
): string {
  const parts = resolveFormatterParts(date, timeZone);
  if (!parts) {
    return '-';
  }

  return `${parts.hour}:${parts.minute}`;
}

export function formatUserDateTime(
  date: Date | string | null | undefined,
  userDateFormat?: string,
  timeZone?: string,
): string {
  const parts = resolveFormatterParts(date, timeZone);
  if (!parts) {
    return '-';
  }

  return `${formatDateParts(parts, userDateFormat)} ${parts.hour}:${parts.minute}`;
}

export function useDateFormat() {
  const { data: preferences } = useUserPreferences();
  const { user } = useCurrentUser();
  const userDateFormat = preferences?.dateFormat || DEFAULT_DATE_FORMAT;
  const userTimeZone = user?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone || DEFAULT_TIMEZONE;

  return {
    dateFormat: userDateFormat,
    timeZone: userTimeZone,
    formatDate: (date: Date | string | null | undefined) =>
      formatUserDate(date, userDateFormat, userTimeZone),
    formatDateTime: (date: Date | string | null | undefined) =>
      formatUserDateTime(date, userDateFormat, userTimeZone),
    formatTime: (date: Date | string | null | undefined) =>
      formatUserTime(date, userTimeZone),
  };
}
